import { useContext } from 'react';
import { FastField, Field } from 'formik';
import { fieldToTextField } from 'formik-mui';
import { InputAdornment, TextField as MuiTextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { AutosaveContext } from '../common/AutosaveContext';
import type { SelectProps as MuiSelectProps, TextFieldProps } from '@mui/material';

const useStyles = makeStyles({ name: 'Select' })({
  nativeDisabledSelect: {
    display: 'none',
  },
  mobileInputAdornment: {
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 0,
  },
  paper: {
    borderRadius: 0,
    boxShadow: 'none',
    marginTop: 0,
  },
});

export interface SDSelectProps {
  readonly useFastField?: boolean;
  readonly SelectProps?: MuiSelectProps;
}

export const Select: React.FC<SDSelectProps & TextFieldProps> = ({
  id, name, children,
  onChange = () => {},
  useFastField = true,
  label = null,
  SelectProps = {},
  ...restProps
}) => {
  const { classes } = useStyles();

  const isMobile = useIsMobile();
  const [autosave] = useContext(AutosaveContext) || [];

  const FieldComponent = useFastField ? FastField : Field;

  return (
    <FieldComponent
      name={name}
    >
      {(props) => (
        <MuiTextField
          {...fieldToTextField(props)}
          {...restProps}
          id={id || name}
          select
          label={label}
          onChange={(e) => {
            props.field.onChange(e);
            onChange(e);

            if (autosave) {
              autosave();
            }
          }}
          InputProps={{
            endAdornment: Boolean(SelectProps?.multiple) && Boolean(isMobile) && (
              <InputAdornment
                position="end"
                className={classes.mobileInputAdornment}
              >
                <ExpandMoreIcon />
              </InputAdornment>
            ),
          }}
          SelectProps={{
            IconComponent: ExpandMoreIcon,
            native: !!isMobile,
            MenuProps: {
              PaperProps: {
                elevation: 0,
                square: true,
                className: classes.paper,
              },
            },
            ...SelectProps,
          }}
        >
          {Boolean(isMobile) && !SelectProps?.multiple && (
            <option
              disabled
              className={classes.nativeDisabledSelect}
            >
              {' '}
            </option>
          )}

          {children}
        </MuiTextField>
      )}
    </FieldComponent>
  );
};

export * from './MenuItem';
